import { useEffect, useRef, useState } from 'react'

// D-VIEWS-STICKY (Maya round table, 2026-08-06): a drop-in replacement for useState that persists
// to sessionStorage keyed per view, so a view's filter/search/sort/page state survives a full page
// reload (F5) and the unmount that happens when the user opens an Rx and comes back. sessionStorage
// (not localStorage) keeps it session-scoped and per-tab — filter values can include patient search
// terms, so they must not linger. Set instances (the column-filter shape) are serialised losslessly.

function replacer(_k: string, v: unknown): unknown {
  return v instanceof Set ? { __set: Array.from(v as Set<unknown>) } : v
}
function reviver(_k: string, v: any): unknown {
  return v && typeof v === 'object' && Array.isArray(v.__set) ? new Set(v.__set) : v
}

// Prefixes owned by view-scoped sticky state. Registered here so that adding a sticky key on a new
// surface is one line away from being cleared at the session boundary — see clearStickyViewState.
// 'ai:' — Support AI conversations (D-SUPPORT-7). Registered here for the same reason as the rest:
// a transcript can contain ticket text the agent pasted or the assistant retrieved, which may carry
// patient content, so it must not outlive the signed-in identity. The keys are ALSO account-scoped
// (`ai:chat:<accountId>`) so a second user cannot rehydrate the first one's thread even before this
// clear runs — belt and braces, because a shared support workstation gets both.
const STICKY_PREFIXES = ['cv:', 'sq:', 'ai:'] as const

/**
 * Drop every view-scoped sticky value. Called when the signed-in identity changes.
 *
 * WHY (2026-08-06 review): sessionStorage is per-tab and dies with the tab, which is the right
 * choice — but a tab OUTLIVES a login. Pharmacies share workstations: pharmacist A filters a queue
 * by a patient name, signs out, pharmacist B signs in on the same tab, and A's search terms
 * rehydrate into B's view. The hook's own contract says these values "must not linger"; a session
 * boundary is exactly where they must stop.
 *
 * Keys are view-scoped (`cv:<viewId>:…`), not user-scoped, so there is nothing to namespace against
 * — they have to be removed.
 */
export function clearStickyViewState(): void {
  try {
    const doomed: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i)
      if (k && STICKY_PREFIXES.some(p => k.startsWith(p))) doomed.push(k)
    }
    doomed.forEach(k => sessionStorage.removeItem(k))
  } catch {
    /* private mode / quota — nothing was persisted, so nothing to clear */
  }
}

function read<T>(key: string | null, fallback: T, store: Storage = sessionStorage): T {
  if (!key) return fallback
  try {
    const raw = store.getItem(key)
    return raw != null ? (JSON.parse(raw, reviver) as T) : fallback
  } catch {
    return fallback
  }
}

function usePersistentState<T>(
  key: string | null,
  initial: T,
  store: () => Storage,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => read(key, initial, store()))
  const keyRef = useRef(key)

  // Re-hydrate when the view key changes (switching views within the same mount).
  useEffect(() => {
    if (key === keyRef.current) return
    keyRef.current = key
    setState(read(key, initial, store()))
    // initial is intentionally excluded — a new key means "load that view's state, else its default".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  // Persist on every change.
  useEffect(() => {
    if (!key) return
    try {
      store().setItem(key, JSON.stringify(state, replacer))
    } catch {
      /* quota / private mode — degrade to in-memory only */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, state])

  return [state, setState]
}

/**
 * useState that persists under `key` in sessionStorage. When `key` changes (the user switches to a
 * different view), the state re-hydrates from that view's own saved value — so views never bleed
 * filters into each other. Pass key = null to disable persistence (plain useState behaviour).
 */
export function useStickyState<T>(
  key: string | null,
  initial: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  return usePersistentState(key, initial, () => sessionStorage)
}

/**
 * Same contract as useStickyState, but backed by localStorage so the value SURVIVES the session.
 *
 * Use this ONLY for pure UI preferences with no patient content in them — D-VIEWS-COLUMN-RESIZE
 * column widths are the intended case (a map of columnKey → px). Filter values, search terms and
 * anything else that can carry a patient name stay on useStickyState: sessionStorage dies with the
 * tab AND is dropped at the identity boundary (clearStickyViewState), which is exactly the
 * protection a shared pharmacy workstation needs. A column width leaking across a sign-out is a
 * shrug; a search term is not.
 *
 * That difference is the whole reason this is a separate export rather than a boolean flag — the
 * choice should be visible at the call site.
 */
export function useDurableState<T>(
  key: string | null,
  initial: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  return usePersistentState(key, initial, () => localStorage)
}
